import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Chierico L2-5 (manuale V1, rr. 2761-2857). 16 mosse.
 *
 * Pattern notabili:
 * - **Cross-class restriction `prerequisites.anyOf`** (FI-19): 4 mosse del
 *   Chierico L2-5 (*Arma Consacrata*, *Protezione Divina*, *Intervento Divino*,
 *   *Guaritore Devoto*) richiedono *Divinità* OR *Divinità della Natura*
 *   (Druido) OR *Ordine Sacro* (Paladino). Pattern già strutturato dallo schema:
 *   `prerequisites: [{ anyOf: [...] }]` (i due ID Druido/Paladino sono
 *   forward-references — il catalogo cross-class li accetta).
 * - **Cross-class restriction in description** (Magia Potenziata): "Se hai
 *   già preso questa mossa come Mago, non puoi prenderla di nuovo".
 * - ***Seminario Sperimentale***: mossa multiclasse con `choices` per il
 *   tipo di seminario (monastero o accademia). Sceglie 1 mossa specifica
 *   ottenibile a runtime — modellata come description-only (le mosse
 *   target sono Mago/Monaco e si gestiranno in level-up).
 */

const allargareLeBasi: Move = {
  id: 'chierico.allargare-le-basi',
  name: 'Allargare le Basi',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Scegli una mossa iniziale del Chierico che non hai già preso.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede una mossa iniziale residua del gruppo del Chierico.
  grantsMoveChoice: { kind: 'from-group', groupId: 'mossa-iniziale-aggiuntiva' },
};

const armaConsacrata: Move = {
  id: 'chierico.arma-consacrata',
  name: 'Arma Consacrata',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger:
    'Quando disponi del tempo e della tranquillità necessari, in un luogo consacrato alla tua divinità',
  description:
    "Puoi benedire un'arma in tuo possesso. Descrivi come questo si riflette sul suo aspetto. Scegli una delle seguenti etichette, che si andrà ad aggiungere a quelle che l'arma ha già: *impatto*, +1 *perforante*, *luminosa* (può emettere il bagliore di una torcia a comando) o *sacra* (ti avverte se nei paraggi c'è una creatura, un oggetto o un luogo contrario alla tua divinità).\n\nOgni volta che **ti *accampi* e preghi la tua divinità**, puoi decidere di passare un po' più di tempo a pregare e cambiare l'etichetta che hai scelto per la tua *Arma Consacrata*. Puoi avere una sola *Arma Consacrata* per volta.",
  tags: [],
  prerequisites: [
    {
      anyOf: ['chierico.divinita', 'druido.divinita-della-natura', 'paladino.ordine-sacro'],
    },
  ],
  replaces: [],
};

const protezioneDivina: Move = {
  id: 'chierico.protezione-divina',
  name: 'Protezione Divina',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando subisci danni da poteri o creature invise alla tua divinità',
  description:
    'Hai +2 Armatura contro i danni provocati da poteri o creature invise alla tua divinità. Inoltre, quando **non indossi armatura né imbracci uno scudo**, hai Armatura 1.',
  tags: [],
  prerequisites: [
    {
      anyOf: ['chierico.divinita', 'druido.divinita-della-natura', 'paladino.ordine-sacro'],
    },
  ],
  replaces: [],
};

const interventoDivino: Move = {
  id: 'chierico.intervento-divino',
  name: 'Intervento Divino',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando ti *accampi* e preghi il tuo dio, oppure tu o un alleato subite danni',
  description:
    'Quando ti *accampi* e preghi il tuo dio, al risveglio perdi tutte le prese che ti sono rimaste e prendi 1.\n\nQuando tu o un alleato subite danni, spendi 1 presa per annullare il danno invocando la tua divinità, che interviene con una manifestazione appropriata (un soffio improvviso di vento, uno scivolone fortuito, un lampo di luce).',
  tags: [],
  prerequisites: [
    {
      anyOf: ['chierico.divinita', 'druido.divinita-della-natura', 'paladino.ordine-sacro'],
    },
  ],
  replaces: [],
};

const primoSoccorso: Move = {
  id: 'chierico.primo-soccorso',
  name: 'Primo Soccorso',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando lanci *Curare Ferite Leggere*',
  description:
    'Puoi provare a lanciare *Curare Ferite Leggere* anche se non hai più prese di *Mana*. Inoltre, se scegli di spendere prese come conseguenza di un 7-9, non ne consumerai alcuna.',
  tags: [],
  prerequisites: ['chierico.lanciare-incantesimi-divini'],
  replaces: [],
};

const prescelto: Move = {
  id: 'chierico.prescelto',
  name: 'Prescelto',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli un incantesimo delle tue Tradizioni Sacre. Da ora in poi, quando lo lanci, costa 1 presa di *Mana* in meno del normale ai fini dei requisiti e delle conseguenze di un 7-9.',
  tags: [],
  prerequisites: ['chierico.lanciare-incantesimi-divini'],
  replaces: [],
};

const penitenza: Move = {
  id: 'chierico.penitenza',
  name: 'Penitenza',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando subisci danno e ti immergi nel dolore',
  description:
    'Puoi decidere di subire 1d4 danni extra (con etichetta *ignora armatura*). Se lo fai, prendi +1 al prossimo tiro di *lanciare incantesimi*.',
  tags: [],
  prerequisites: ['chierico.lanciare-incantesimi-divini'],
  replaces: [],
};

const serenita: Move = {
  id: 'chierico.serenita',
  name: 'Serenità',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando lanci un incantesimo',
  description:
    'Sei in grado di dividere efficacemente il tuo potere: ignori la prima penalità di -1 causata dal mantenere incantesimi *continuati*.',
  tags: [],
  prerequisites: ['chierico.lanciare-incantesimi-divini'],
  replaces: [],
};

const magiaPotenziata: Move = {
  id: 'chierico.magia-potenziata',
  name: 'Magia Potenziata',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando tiri per *lanciare incantesimi*',
  description:
    "Con 10+, se accetti di subire una complicazione dalla lista del 7-9, scegli un potenziamento tra questi (se applicabile, e con conseguenze a discrezione del GM):\n\n- gli effetti numerici sono massimizzati, gli altri potenziati;\n- l'effetto interessa un numero doppio di bersagli o un'area più vasta.\n\nGli effetti si applicano sia che tu usi *lanciare incantesimi* o un'altra mossa che richieda di «*Tirare come per* lanciare incantesimi».\n\nSe hai già preso questa mossa come Mago, non puoi prenderla di nuovo!",
  tags: [],
  prerequisites: ['chierico.lanciare-incantesimi-divini'],
  replaces: [],
};

const celebrazioneDivina: Move = {
  id: 'chierico.celebrazione-divina',
  name: 'Celebrazione Divina',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando offici un rituale per il tuo dio alla presenza di un nutrito gruppo di fedeli',
  description:
    'Prendi 1 presa. Puoi spenderla per trasformare un risultato di 7-9 su *Lanciare Incantesimi Divini* in un 10+, o per tirare di nuovo in caso di fallimento.',
  tags: [],
  prerequisites: ['chierico.lanciare-incantesimi-divini'],
  replaces: [],
};

const rinvigorire: Move = {
  id: 'chierico.rinvigorire',
  name: 'Rinvigorire',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger:
    'Quando improvvisi un incantesimo di guarigione di livello pari a uno *continuato* che tieni attivo',
  description:
    "Quest'ultimo ha fine, e tu curi al suo bersaglio (a te stesso, se il bersaglio dell'effetto non era un alleato) un numero di PF pari al livello dell'incantesimo cui hai posto fine +1.",
  tags: [],
  prerequisites: ['chierico.lanciare-incantesimi-divini'],
  replaces: [],
};

const guaritoreDevoto: Move = {
  id: 'chierico.guaritore-devoto',
  name: 'Guaritore Devoto',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando guarisci qualcuno con un incantesimo (anche te stesso)',
  description: "Aggiungi il livello dell'incantesimo +3 ai PF curati.",
  tags: [],
  prerequisites: [
    {
      anyOf: ['chierico.divinita', 'druido.divinita-della-natura', 'paladino.ordine-sacro'],
    },
  ],
  replaces: [],
};

const seminarioSperimentale: Move = {
  id: 'chierico.seminario-sperimentale',
  name: 'Seminario Sperimentale',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Il tuo seminario proponeva anche una formazione accademica o era un monastero (vedi `choices`). Ottieni una *Regola* (*della Battaglia*, *del Lavoro* o *della Pietà*) del Monaco (pagina 147), oppure una mossa a tua scelta tra *Scuola per Giovani Maghi* o *Scudo Magico* del Mago (pagina 137).',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-44 (2026-05-15, manuale V1 r. 2837): scelta tra 5 mosse cross-class
  // specifiche (3 Regole Monaco + 2 mosse Mago). Pattern self-mapping con
  // `grantsMoveChoice from-choice-mapping` (come `paladino.ordine-sovrannaturale`).
  // Le 5 choices sono granulari per mappatura diretta; la struttura UI può
  // raggrupparle per "tipo di seminario" (Monastero vs Accademia).
  choices: [
    {
      id: 'regola-battaglia',
      label: 'Monastero · Regola della Battaglia',
      description: 'Ottieni *Regola della Battaglia* del Monaco.',
    },
    {
      id: 'regola-lavoro',
      label: 'Monastero · Regola del Lavoro',
      description: 'Ottieni *Regola del Lavoro* del Monaco.',
    },
    {
      id: 'regola-pieta',
      label: 'Monastero · Regola della Pietà',
      description: 'Ottieni *Regola della Pietà* del Monaco.',
    },
    {
      id: 'scuola-giovani-maghi',
      label: 'Accademia · Scuola per Giovani Maghi',
      description: 'Ottieni *Scuola per Giovani Maghi* del Mago.',
    },
    {
      id: 'scudo-magico',
      label: 'Accademia · Scudo Magico',
      description: 'Ottieni *Scudo Magico* del Mago.',
    },
  ],
  choiceCount: 1,
  grantsMoveChoice: {
    kind: 'from-choice-mapping',
    sourceMoveId: 'chierico.seminario-sperimentale',
    mapping: {
      'regola-battaglia': 'monaco.regola-della-battaglia',
      'regola-lavoro': 'monaco.regola-del-lavoro',
      'regola-pieta': 'monaco.regola-della-pieta',
      'scuola-giovani-maghi': 'mago.scuola-per-giovani-maghi',
      'scudo-magico': 'mago.scudo-magico',
    },
  },
};

const conoscenzeClericali: Move = {
  id: 'chierico.conoscenze-clericali',
  name: 'Conoscenze Clericali',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger:
    'Quando *declami conoscenze* o *discerni la realtà* in merito a qualcosa di religioso o divino',
  description:
    'Con 10+, il GM ti rivelerà anche un segreto poco conosciuto riguardo quel qualcosa.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const unOffertaPerUnConsiglio: Move = {
  id: 'chierico.un-offerta-per-un-consiglio',
  name: "Un'Offerta per un Consiglio",
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger:
    'Quando sacrifichi qualcosa di significativo per te e la tua divinità (a discrezione del GM), pregando per avere consiglio riguardo una decisione importante',
  description:
    'Il dio ti dirà cosa vorrebbe tu facessi. Se lo fai, guadagni un PE, ma non più di una volta a sessione.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const guidareIlGregge: Move = {
  id: 'chierico.guidare-il-gregge',
  name: 'Guidare il Gregge',
  type: 'advanced_2_5',
  classId: 'chierico',
  trigger: 'Quando predichi alla folla',
  description:
    "Tira+**CAR**. Con un 10+, prendi 3. Con 7-9, invece, prendi 1. Spendi una presa per ottenere uno dei seguenti effetti:\n\n- ti consegnano qualcuno che stavi cercando;\n- si offrono di donarti i beni o il denaro di cui hai bisogno, compatibilmente con le loro possibilità;\n- ti seguono e combattono per te;\n- si abbandonano a un'emozione violenta e incontrollata: gioia, dolore o rabbia, come preferisci;\n- ritornano silenziosamente alle loro vite;\n- intonano inni di lode alla tua divinità (il GM stabilirà con quali conseguenze). Se hai *Celebrazione Divina* ottieni una presa per tale mossa.\n\nCon 6-, la folla ti si rivolta contro.",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Prendi 3 prese da spendere sugli effetti.' },
    on7to9: { text: 'Prendi 1 presa da spendere su un effetto.' },
    on6minus: { text: 'La folla ti si rivolta contro.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const chiericoAdvanced2to5Moves: readonly Move[] = [
  allargareLeBasi,
  armaConsacrata,
  protezioneDivina,
  interventoDivino,
  primoSoccorso,
  prescelto,
  penitenza,
  serenita,
  magiaPotenziata,
  celebrazioneDivina,
  rinvigorire,
  guaritoreDevoto,
  seminarioSperimentale,
  conoscenzeClericali,
  unOffertaPerUnConsiglio,
  guidareIlGregge,
] as const;
